import { Suspense } from "react";
import { ActivityRegistrationNumberInput } from "@shared-kernel/primary/shared/activity-registration-number-input/activity-registration-number-input";
import type { Control, FieldValues, Path } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";
import { Input } from "@components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared-kernel/primary/shared/shadcn/ui/select";
import { Skeleton } from "@shared-kernel/primary/shared/shadcn/ui/skeleton";
import { PhoneInput } from "@user-management-context/primary/shared/phone-input/phone-input";
import { SiretAutocompleteField } from "@shared-kernel/primary/shared/siret-autocomplete/siret-autocomplete-field";
import { SUPPORTED_COUNTRY, COUNTRY_LABELS } from "@shared-kernel/domain/types/supported-country";
import type { ProviderInformationBase } from "../types/provider-training-form.types";

const ALL_COUNTRIES = Object.values(SUPPORTED_COUNTRY);

interface ProviderInformationFormSectionProps<TFormValues extends FieldValues & ProviderInformationBase> {
  control: Control<TFormValues>;
  showSiretAndNda?: boolean;
}

/**
 * Shared form section for provider information fields.
 * Extracts the 6 provider fields used in both:
 * - RequestDataSection (provider portal)
 * - ExternalProviderBatchRequestForm (student form)
 */
export const ProviderInformationFormSection = <TFormValues extends FieldValues & ProviderInformationBase>({
  control,
  showSiretAndNda = true,
}: ProviderInformationFormSectionProps<TFormValues>) => {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        {showSiretAndNda ? (
          <>
            <FormField
              control={control}
              name={"providerCountry" as Path<TFormValues>}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Pays du prestataire</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className={fieldState.error ? "border-destructive/50" : undefined}>
                        <SelectValue>
                          {field.value && COUNTRY_LABELS[field.value as SUPPORTED_COUNTRY] ? (
                            <span className="flex items-center gap-2">
                              <span>{COUNTRY_LABELS[field.value as SUPPORTED_COUNTRY].flag}</span>
                              <span>{COUNTRY_LABELS[field.value as SUPPORTED_COUNTRY].label}</span>
                            </span>
                          ) : null}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ALL_COUNTRIES.map(code => {
                        const { label, flag } = COUNTRY_LABELS[code];
                        return (
                          <SelectItem key={code} value={code}>
                            <span className="flex items-center gap-2">
                              <span>{flag}</span>
                              <span>{label}</span>
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <SiretAutocompleteField
              siretFieldName="providerSiret"
              countryFieldName="providerCountry"
              nameFieldName="providerName"
              label="N° SIRET"
            />
            <FormField
              control={control}
              name={"providerName" as Path<TFormValues>}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom du prestataire</FormLabel>
                  <FormControl>
                    <Input type="text" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={"providerActivityRegistrationNumber" as Path<TFormValues>}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>N° de déclaration d'activité (NDA) (optionnel)</FormLabel>
                  <FormControl>
                    <ActivityRegistrationNumberInput {...field} placeholder="11 chiffres" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : (
          <FormField
            control={control}
            name={"providerName" as Path<TFormValues>}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nom du prestataire</FormLabel>
                <FormControl>
                  <Input type="text" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={control}
          name={"providerEmail" as Path<TFormValues>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={"providerPhone" as Path<TFormValues>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Téléphone</FormLabel>
              <FormControl>
                <Suspense fallback={<Skeleton className="h-10 w-full" />}>
                  <PhoneInput phone={field.value} onChange={field.onChange} />
                </Suspense>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          control={control}
          name={"providerContactFirstName" as Path<TFormValues>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Prénom du contact</FormLabel>
              <FormControl>
                <Input type="text" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={"providerContactLastName" as Path<TFormValues>}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nom du contact</FormLabel>
              <FormControl>
                <Input type="text" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
